import { useEffect, useState } from "react";
import { X, Check, PackageCheck, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtQty, inr } from "../../spare-parts/lib";

/**
 * Quick "Issue from stock" dialog for the JC Parts tab.
 * User picks an inventory part (search-typeahead), sets qty, and confirms.
 * Backend deducts stock, releases any reservation, marks the parts_needed
 * row status → issued.
 */
export default function IssuePartDialog({ open, onClose, jobCardId, partsNeededRow, onIssued }) {
  const [parts, setParts] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPicked(null); setError(null); setNotes("");
    // Pre-fill from parts_needed row
    if (partsNeededRow) {
      setQ(partsNeededRow.name || "");
      setQty(Number(partsNeededRow.qty) || 1);
    }
    api.get("/spares/parts", { params: { limit: 500 } }).then((r) => {
      const all = r.data || [];
      setParts(all);
      // Try to auto-pick based on part_number match
      if (partsNeededRow?.part_number) {
        const m = all.find((p) => (p.part_number || "").toLowerCase() === partsNeededRow.part_number.toLowerCase());
        if (m) setPicked(m);
      }
    });
  }, [open, partsNeededRow]);

  if (!open) return null;

  const results = q
    ? parts.filter((p) => (p.name || "").toLowerCase().includes(q.toLowerCase()) ||
                          (p.part_number || "").toLowerCase().includes(q.toLowerCase())).slice(0, 8)
    : parts.slice(0, 8);

  const canSubmit = picked && Number(qty) > 0 && Number(qty) <= (picked.stock_qty || 0);

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      await api.post("/spares/job-card-issues", {
        job_card_id: jobCardId,
        items: [{
          part_id: picked.id,
          qty: Number(qty),
          parts_needed_id: partsNeededRow?.id || null,
        }],
        notes,
      });
      toast.success(`Issued ${qty} × ${picked.name}`);
      onIssued?.();
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not issue");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="jc-issue-dialog-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
           data-testid="jc-issue-dialog">
        <header className="px-6 py-4 border-b border-[#E4E7EC] flex items-center justify-between bg-[#F7F9FC]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#12B76A]/15 text-[#0F9256] grid place-items-center">
              <PackageCheck size={14} />
            </div>
            <div>
              <h2 className="text-[15px] font-bold text-[#1d3557]">Issue from stock</h2>
              <p className="text-[11px] text-[#667085]">Deduct inventory · release reservation.</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-[#E4E7EC] grid place-items-center text-[#667085]"
                  data-testid="jc-issue-close">
            <X size={17} />
          </button>
        </header>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Search stock part *</span>
            <div className="mt-1 relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a8dadc]" />
              <input value={picked ? picked.name : q}
                     onChange={(e) => { setQ(e.target.value); setPicked(null); }}
                     placeholder="Type part name or number"
                     data-testid="jc-issue-search"
                     className="w-full h-10 pl-9 pr-3 rounded-md border border-[#E4E7EC] text-[13px]" />
            </div>
            {!picked && q && results.length > 0 && (
              <ul className="mt-1 border border-[#E4E7EC] rounded-md max-h-52 overflow-y-auto text-[12.5px] bg-white">
                {results.map((p) => {
                  const outOfStock = (p.available_qty ?? p.stock_qty ?? 0) <= 0;
                  return (
                    <li key={p.id}>
                      <button type="button" disabled={outOfStock}
                              onClick={() => setPicked(p)}
                              data-testid={`jc-issue-pick-${p.id}`}
                              className={`w-full text-left px-3 py-2 hover:bg-[#F1FAEE] ${outOfStock ? "opacity-40 cursor-not-allowed" : ""}`}>
                        <div className="font-semibold text-[#1d3557]">{p.name}</div>
                        <div className="text-[10.5px] text-[#667085]">
                          {p.part_number || ""} · avail <b>{p.available_qty}</b> · stock {p.stock_qty}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {picked && (
              <div className="mt-1 rounded-md bg-[#F1FAEE] border border-[#a8dadc]/40 px-3 py-2 text-[12px]">
                <div className="font-semibold text-[#1d3557]">{picked.name}</div>
                <div className="text-[10.5px] text-[#0F9256]">
                  In stock: <b>{fmtQty(picked.stock_qty, picked.unit)}</b>
                  · Reserved: {picked.reserved_qty || 0}
                  · Available: <b>{picked.available_qty}</b>
                </div>
              </div>
            )}
          </label>
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Quantity *</span>
            <input type="number" min={1} value={qty}
                   onChange={(e) => setQty(Number(e.target.value) || 0)}
                   data-testid="jc-issue-qty"
                   className="mt-1 h-11 px-3 rounded-md border border-[#E4E7EC] text-[15px] font-mono tabular-nums w-full" />
            {picked && Number(qty) > (picked.stock_qty || 0) && (
              <div className="text-[11px] text-[#e63946] mt-1">
                Only {picked.stock_qty} in stock — cannot issue {qty}.
              </div>
            )}
          </label>
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Notes</span>
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
                   placeholder="Fitted by mechanic X…"
                   data-testid="jc-issue-notes"
                   className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
          </label>
          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]"
                 data-testid="jc-issue-error">
              {error}
            </div>
          )}
        </div>
        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white">
            Cancel
          </button>
          <button onClick={submit} disabled={!canSubmit || saving}
                  data-testid="jc-issue-submit"
                  className="h-10 px-5 rounded-md bg-[#12B76A] hover:bg-[#0F9256] text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <Check size={13} /> {saving ? "Issuing…" : "Confirm issue"}
          </button>
        </footer>
      </div>
    </div>
  );
}
